import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

import LoginModal1 from '../../components/Header/LoginModal1';
import CommonSignUpModal from '../../components/Header/CommonSignUpModal';
import strings from '../../constant/strings';
import { setReturnTo } from '../../utils/returnTo';
import { readDraft } from './utils';
import PreorderOtpForm from './PreorderOtpForm';
import PreorderSteps from './PreorderSteps';
import PreorderTopBar from './PreorderTopBar';

/**
 * The sign-in step of the pre-order flow, on its own page.
 *
 * It exists separately from /login because this visitor arrived from a social
 * post to pre-order a device: they can only be a patient, so asking "Login
 * as... Doctor / Clinician / Sub-Administration" is a question with one
 * possible answer and three ways to get lost. The shared login page still asks
 * everyone else, untouched.
 *
 * The modals themselves are the existing ones; this page only fixes the
 * account type and says why the visitor is here.
 */
const PageWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  & > div > div {
    max-height: none !important;
    height: auto !important;
  }
`;

const Activated = styled.div`
  display: inline-block;
  background: #EAF7F1;
  color: #15795A;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 14px;
`;

/* This page sits outside the Page container, so the bar needs its own width. */
const FlowHead = styled.div`
  max-width: 640px;
  margin: 0 auto;
  padding: 28px 16px 0;

  @media (max-width: 600px) {
    padding: 20px 16px 0;
  }
`;

const Intro = styled.div`
  text-align: center;
  padding: 28px 16px 0;
  font-family: 'Poppins', sans-serif;

  h1 {
    font-size: 20px;
    font-weight: 600;
    color: #2B2B2B;
    margin: 0 0 6px;
  }

  p {
    font-size: 14px;
    color: #7A7A8C;
    margin: 0;
    line-height: 1.6;
  }

  @media (max-width: 600px) {
    padding: 20px 16px 0;
    h1 { font-size: 18px; }
  }
`;

const PreorderAuth = () => {
  const history = useHistory();
  const location = useLocation();
  const { slug } = useParams();
  const token = useSelector((state) => state.auth.token);
  /*
   * Opens on signup, not sign-in. This page is only reached from a pre-order
   * that someone found on social media, so almost everyone arriving has no
   * account - showing them a login form first cost a click for the majority to
   * save one for the minority. The sign-in link is still right there.
   */
  const [isSignUpModal, setSignUpModal] = useState(true);
  // Set once a signup succeeds: the page then asks for the emailed code
  // instead of telling them to go and find a link.
  const [awaitingCodeFor, setAwaitingCodeFor] = useState('');
  // Set by the activation link in the signup email, so the patient is told
  // their account is live rather than just being shown a login form again.
  const justActivated = new URLSearchParams(location.search).get('activated') === '1';
  // What they filled in on the checkout before being asked to sign in.
  const draft = useMemo(() => readDraft(), []);

  // Back to the product page, which is where reserving happens now.
  const returnPath = `/preorder/${slug}`;

  useEffect(() => {
    document.body.style.overflow = 'auto';
    return () => { document.body.style.overflow = 'auto'; };
  }, []);

  useEffect(() => {
    // Already signed in - there is nothing to ask, so carry on to checkout.
    if (token) {
      history.replace(returnPath);
      return;
    }
    // Recorded so the login saga returns them here rather than to the
    // dashboard. Set on this page too, not only on the landing page, so a
    // reload or a shared link still lands back in the flow.
    setReturnTo(returnPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, returnPath]);

  const toggleLogin = () => setSignUpModal((open) => !open);

  if (token) return null;

  // Waiting on the emailed code. Nothing else on the page is useful now.
  if (awaitingCodeFor) {
    return (
      <>
        <FlowHead>
          <PreorderTopBar />
          <PreorderSteps current={2} />
        </FlowHead>
        <Intro>
          <h1>{strings.confirmYourEmail}</h1>
          <p>{strings.confirmYourEmailNote}</p>
        </Intro>
        <PreorderOtpForm
          email={awaitingCodeFor}
          onBack={() => setAwaitingCodeFor('')}
        />
      </>
    );
  }

  return (
    <>
      <FlowHead>
        <PreorderTopBar />
        <PreorderSteps current={1} />
      </FlowHead>
      <Intro>
        {justActivated ? <Activated>{strings.accountActivated}</Activated> : null}
        <h1>{isSignUpModal ? strings.preorderCreateAccount : strings.preorderSignIn}</h1>
        <p>{justActivated ? strings.accountActivatedNote : strings.signInToPreorderNote}</p>
      </Intro>
      <PageWrapper>
        {!isSignUpModal ? (
          <LoginModal1
            isSignUpModal={isSignUpModal}
            setLoginToggle={() => history.push(`/preorder/${slug}`)}
            loginToggle={true}
            toggleLogin={toggleLogin}
            forceUserType="patient"
          />
        ) : (
          <CommonSignUpModal
            isSignUpModal={isSignUpModal}
            setSignUpModal={setSignUpModal}
            toggleSignUp={toggleLogin}
            forcePatient
            onSignedUp={(email) => setAwaitingCodeFor(email)}
            extraSignupFields={{
              preorderSlug: slug,
              // Carried to the server so the order can be placed the moment
              // the account is activated, even from another device.
              ...(draft ? { preorderDraft: {
                paymentPlan: draft.paymentPlan,
                qty: draft.qty,
                shippingAddress: draft.shippingAddress,
                consents: draft.consents,
                attribution: draft.attribution,
              } } : {}),
            }}
          />
        )}
      </PageWrapper>
    </>
  );
};

export default PreorderAuth;
